import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Tooltip,
  Paper,
  TablePagination,
  Card,
  CardContent,
  Button,
  TableSortLabel,
} from "@mui/material";
import InventoryIcon from "@mui/icons-material/Inventory";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import WarningIcon from "@mui/icons-material/Warning";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import DownloadIcon from "@mui/icons-material/Download";
import * as XLSX from "xlsx";
import FilterComponent from "./Components/FiltersStorageTabel.component";
import { useGetStockMetrics } from "../../client/hooks/stock/stock.queries";
import moment from "moment";
import TablePaginationActions from "../components/TablePaginationAction";
import AccessGuard from "../../components/AccessControl/AccessGuard";

const StorageDashboard = () => {
  const maxOC = 2500;
  const maxEstoque = 10000;
  const [showFilters, setShowFilters] = useState(false);
  const [estoqueAtual, setEstoqueAtual] = useState({ min: 0, max: maxEstoque });
  const [estoqueOC, setEstoqueOC] = useState({ min: 0, max: maxOC });

  // Estados temporários (para os campos de input)
  const [filterText, setFilterText] = useState("");
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  // const [selectedDeposito, setSelectedDeposito] = useState("all");

  // Estados aplicados (usados na requisição da API)
  const [appliedFilterText, setAppliedFilterText] = useState("");
  const [appliedChannel, setAppliedChannel] = useState(null);
  const [appliedStartDate, setAppliedStartDate] = useState(null);
  const [appliedEndDate, setAppliedEndDate] = useState(null);
  // const [appliedDeposito, setAppliedDeposito] = useState("all");

  // Filtros locais
  const [statusFilter, setStatusFilter] = useState("all");

  const tableContainerRef = useRef(null);
  const [orderBy, setOrderBy] = useState("");
  const [order, setOrder] = useState("asc");

  const [page, setPage] = useState({ page_count: 20, page_index: 0 });
  const [filteredData, setFilteredData] = useState([]);

  const canaisVenda = [{ label: "MERCADO_LIVRE" }];

  // Parâmetros para a API (usa os estados aplicados)
  const stockParams = {
    channel: appliedChannel?.label || "all",
    search: appliedFilterText || null,
    startDate: appliedStartDate ? appliedStartDate.format("YYYY-MM-DD") : null,
    endDate: appliedEndDate ? appliedEndDate.format("YYYY-MM-DD") : null,
    // local: appliedDeposito !== "all" ? appliedDeposito : null, // FILTRO DE LOCAL DESABILITADO
    page: page.page_index,
    limit: page.page_count,
  };

  // Hook para buscar dados da API
  const { data, isLoading } = useGetStockMetrics(stockParams, true);

  const handleSearch = () => {
    // Aplica os filtros temporários para fazer a requisição
    setAppliedFilterText(filterText);
    setAppliedChannel(selectedChannel);
    setAppliedStartDate(startDate);
    setAppliedEndDate(endDate);
    // setAppliedDeposito(selectedDeposito); // FILTRO DE LOCAL DESABILITADO
    setPage({ ...page, page_index: 0 }); // Reseta para primeira página
  };

  const handleResetFilters = () => {
    // Limpa filtros temporários
    setEstoqueAtual({ min: 0, max: maxEstoque });
    setEstoqueOC({ min: 0, max: maxOC });
    setFilterText("");
    setSelectedChannel(null);
    // setSelectedDeposito("all"); // FILTRO DE LOCAL DESABILITADO
    setStatusFilter("all");
    setStartDate(null);
    setEndDate(null);
    setOrderBy("");
    setOrder("asc");

    // Limpa filtros aplicados
    setAppliedFilterText("");
    setAppliedChannel(null);
    setAppliedStartDate(null);
    setAppliedEndDate(null);
    // setAppliedDeposito("all"); // FILTRO DE LOCAL DESABILITADO
  };

  // Função para ordenar os dados
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  // Função para exportar para Excel
  const handleExportToExcel = () => {
    const exportData = filteredData.map((item) => ({
      Código: item?.partnerId || "N/A",
      Depósito: item?.local || "N/A",
      Descrição: item?.title || "N/A",
      Fornecedor: item?.supplier || "N/A",
      Fat90d: item?.quantity_sold_90_days || 0,
      Fat30d: item?.quantity_sold_30_days || 0,
      "Estoque Atual": item?.actual_stock || 0,
      "Última Venda": item?.last_sale
        ? moment(item.last_sale).format("DD/MM/YYYY")
        : "N/A",
      "Dias Última Venda": item?.last_sale_days || 0,
      "Custo Un": item?.unit_cost || "0,00",
      "Dias Estoque": item?.stock_days || 0,
      "R$ Estoque": item?.stock_value || "0,00",
      "Em OC": item?.unit_purchase_order || 0,
      "Dias Estoque + OC": item?.stock_days_plus_purchase_order || 0,
      Status: item.actual_stock === 0 ? "Inativo" : "Ativo",
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Estoque");

    // Aplicar larguras de coluna
    const colWidths = [
      { wch: 15 }, // Código
      { wch: 15 }, // Depósito
      { wch: 40 }, // Descrição
      { wch: 25 }, // Fornecedor
      { wch: 10 }, // Fat90d
      { wch: 10 }, // Fat30d
      { wch: 15 }, // Estoque Atual
      { wch: 15 }, // Última Venda
      { wch: 18 }, // Dias Última Venda
      { wch: 12 }, // Custo Un
      { wch: 15 }, // Dias Estoque
      { wch: 15 }, // R$ Estoque
      { wch: 10 }, // Em OC
      { wch: 18 }, // Dias Estoque + OC
      { wch: 10 }, // Status
    ];
    worksheet["!cols"] = colWidths;

    XLSX.writeFile(
      workbook,
      `Gestao_Estoque_${moment().format("DD-MM-YYYY")}.xlsx`,
    );
  };

  const handleChangePage = (_event, newPage) => {
    setPage({ ...page, page_index: newPage });
    if (tableContainerRef.current) {
      tableContainerRef.current.scrollTop = 0;
    }
  };

  const handleChangeRowsPerPage = (event) => {
    setPage({
      page_count: parseInt(event.target.value, 10),
      page_index: 0,
    });
    if (tableContainerRef.current) {
      tableContainerRef.current.scrollTop = 0;
    }
  };
  // Funções de manipulação de canais removidas

  // FILTRO DE LOCAL DESABILITADO - Extrair depósitos únicos dos dados para o dropdown
  // const depositos = useMemo(() => {
  //   if (!data?.data) return [];
  //   const uniqueDepositos = [
  //     ...new Set(data.data.map((item) => item.local).filter(Boolean)),
  //   ];
  //   return uniqueDepositos.sort();
  // }, [data]);
  const depositos = []; // Array vazio enquanto filtro está desabilitado

  // Filtra os dados quando houver mudanças nos filtros ou dados
  // Nota: filterText agora é filtrado no back-end via parâmetro 'search'
  useEffect(() => {
    if (data?.data) {
      let filtered = data.data.filter((item) => {
        const withinEstoqueAtual =
          item.actual_stock >= estoqueAtual.min &&
          item.actual_stock <= estoqueAtual.max;

        const withinEstoqueOC =
          item.unit_purchase_order >= estoqueOC.min &&
          item.unit_purchase_order <= estoqueOC.max;

        // Filtro de status (local/visual)
        const matchesStatus =
          statusFilter === "all" ||
          (statusFilter === "active" && item.actual_stock > 0) ||
          (statusFilter === "inactive" && item.actual_stock === 0);

        return withinEstoqueAtual && withinEstoqueOC && matchesStatus;
      });

      // Aplicar ordenação
      if (orderBy) {
        filtered = filtered.sort((a, b) => {
          let aValue = a[orderBy];
          let bValue = b[orderBy];

          // Tratamento especial para valores numéricos
          if (
            orderBy === "actual_stock" ||
            orderBy === "quantity_sold_90_days" ||
            orderBy === "quantity_sold_30_days" ||
            orderBy === "last_sale_days" ||
            orderBy === "stock_days" ||
            orderBy === "unit_purchase_order" ||
            orderBy === "stock_days_plus_purchase_order"
          ) {
            aValue = parseFloat(aValue) || 0;
            bValue = parseFloat(bValue) || 0;
          }

          // Tratamento para strings
          if (typeof aValue === "string") {
            aValue = aValue.toLowerCase();
            bValue = bValue?.toLowerCase() || "";
          }

          if (order === "asc") {
            return aValue > bValue ? 1 : -1;
          } else {
            return aValue < bValue ? 1 : -1;
          }
        });
      }

      setFilteredData(filtered);
    }
  }, [
    data,
    estoqueAtual,
    estoqueOC,
    // selectedDeposito, // FILTRO DE LOCAL DESABILITADO
    statusFilter,
    orderBy,
    order,
  ]);

  const getBackgroundColor = (diasUltimaVenda) => {
    if (diasUltimaVenda <= 7) return "#ffffff";
    if (diasUltimaVenda >= 8 && diasUltimaVenda <= 14) return "#f5f5f5";
    if (diasUltimaVenda >= 15 && diasUltimaVenda <= 30) return "#fef7e0";
    if (diasUltimaVenda >= 31 && diasUltimaVenda <= 60) return "#fff4e6";
    return "#fdf2f2";
  };

  const formatCurrency = (value) => {
    if (!value) return "R$ 0,00";
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  // Dados de resumo
  const summaryData = {
    totalItems: data?.totalItems || 0,
    lowStock:
      filteredData?.filter((item) => item.actual_stock < 10)?.length || 0,
    pendingOrders:
      filteredData?.filter((item) => item.unit_purchase_order > 0)?.length || 0,
    totalValue:
      filteredData?.reduce(
        (acc, item) =>
          acc +
          parseFloat(
            item.stock_value?.replace(/[^\d,]/g, "").replace(",", ".") || 0,
          ),
        0,
      ) || 0,
  };

  const tableHeaders = [
    { id: "partnerId", label: "Código", sortable: true },
    { id: "local", label: "Depósito", sortable: true },
    { id: "title", label: "Descrição", sortable: true },
    { id: "supplier", label: "Fornecedor", sortable: true },
    { id: "quantity_sold_90_days", label: "Fat90d", sortable: true },
    { id: "quantity_sold_30_days", label: "Fat30d", sortable: true },
    { id: "actual_stock", label: "Estoque Atual", sortable: true },
    { id: "last_sale", label: "Última Venda", sortable: true },
    { id: "last_sale_days", label: "Dias Última Venda", sortable: true },
    { id: "unit_cost", label: "Custo Un", sortable: true },
    { id: "stock_days", label: "Dias Estoque", sortable: true },
    { id: "stock_value", label: "R$ Estoque", sortable: true },
    { id: "unit_purchase_order", label: "Em OC", sortable: true },
    {
      id: "stock_days_plus_purchase_order",
      label: "Dias Estoque + OC",
      sortable: true,
    },
    { id: "prog", label: "Prog", sortable: false },
  ];

  return (
    <AccessGuard feature="stock-analysis">
      <style>{`
        @keyframes pulse {
          0% { opacity: 1; }
          50% { opacity: 0.4; }
          100% { opacity: 1; }
        }
      `}</style>
      {/* Cabeçalho da página */}
      <Box sx={{ bgcolor: "#fff", borderBottom: "1px solid #e0e0e0" }}>
        <Box sx={{ p: 3 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: "#ff9800",
                  mr: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <InventoryIcon sx={{ fontSize: "1.8rem", color: "white" }} />
              </Box>
              <Box>
                <Typography
                  variant="h4"
                  component="h1"
                  sx={{ fontWeight: 600, color: "#424242", mb: 0.5 }}
                >
                  Gestão de Estoque
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Controle e monitore seus produtos em estoque
                </Typography>
              </Box>
            </Box>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleExportToExcel}
              sx={{
                bgcolor: "#4caf50",
                "&:hover": { bgcolor: "#45a049" },
              }}
            >
              Exportar Excel
            </Button>
          </Box>

          {/* Cards de resumo */}
          <Box sx={{ display: "flex", gap: 2, mt: 3 }}>
            <Card
              sx={{
                flex: 1,
                borderRadius: 2,
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              }}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      Total de Itens
                    </Typography>
                    <Typography
                      variant="h4"
                      sx={{ fontWeight: 600, color: "#424242" }}
                    >
                      {summaryData.totalItems}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: "#fff3e0",
                      color: "#ff9800",
                    }}
                  >
                    <InventoryIcon sx={{ fontSize: 24 }} />
                  </Box>
                </Box>
              </CardContent>
            </Card>

            <Card
              sx={{
                flex: 1,
                borderRadius: 2,
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              }}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      Estoque Baixo
                    </Typography>
                    <Typography
                      variant="h4"
                      sx={{ fontWeight: 600, color: "#424242" }}
                    >
                      {summaryData.lowStock}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: "#fff3e0",
                      color: "#ff9800",
                    }}
                  >
                    <WarningIcon sx={{ fontSize: 24 }} />
                  </Box>
                </Box>
              </CardContent>
            </Card>

            <Card
              sx={{
                flex: 1,
                borderRadius: 2,
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              }}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      Pedidos Pendentes
                    </Typography>
                    <Typography
                      variant="h4"
                      sx={{ fontWeight: 600, color: "#424242" }}
                    >
                      {summaryData.pendingOrders}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: "#e3f2fd",
                      color: "#2196f3",
                    }}
                  >
                    <LocalShippingIcon sx={{ fontSize: 24 }} />
                  </Box>
                </Box>
              </CardContent>
            </Card>

            <Card
              sx={{
                flex: 1,
                borderRadius: 2,
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              }}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      Valor Total
                    </Typography>
                    <Typography
                      variant="h5"
                      sx={{ fontWeight: 600, color: "#424242" }}
                    >
                      {formatCurrency(summaryData.totalValue)}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: "#e8f5e8",
                      color: "#4caf50",
                    }}
                  >
                    <TrendingUpIcon sx={{ fontSize: 24 }} />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>
      </Box>

      <Box sx={{ p: 3 }}>
        {/* Filtros */}
        <Box sx={{ mb: 3 }}>
          <FilterComponent
            showFilters={showFilters}
            setShowFilters={setShowFilters}
            estoqueAtual={estoqueAtual}
            setEstoqueAtual={setEstoqueAtual}
            estoqueOC={estoqueOC}
            setEstoqueOC={setEstoqueOC}
            filterText={filterText}
            setFilterText={setFilterText}
            handleResetFilters={handleResetFilters}
            canaisVenda={canaisVenda}
            selectedChannel={selectedChannel}
            setSelectedChannel={setSelectedChannel}
            // selectedDeposito={selectedDeposito} // FILTRO DE LOCAL DESABILITADO
            // setSelectedDeposito={setSelectedDeposito} // FILTRO DE LOCAL DESABILITADO
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            depositos={depositos}
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            onSearch={handleSearch}
          />
        </Box>

        {/* Tabela */}
        <Paper
          sx={{
            borderRadius: 2,
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            border: "1px solid #e0e0e0",
          }}
        >
          <TableContainer
            ref={tableContainerRef}
            sx={{ maxHeight: "calc(100vh - 300px)" }}
          >
            <Table stickyHeader>
              <TableHead>
                <TableRow sx={{ bgcolor: "#f8f9fa" }}>
                  {tableHeaders.map((header) => (
                    <TableCell
                      key={header.id}
                      align="center"
                      sx={{
                        fontWeight: 600,
                        color: "#424242",
                        fontSize: "0.875rem",
                      }}
                    >
                      {header.sortable ? (
                        <TableSortLabel
                          active={orderBy === header.id}
                          direction={orderBy === header.id ? order : "asc"}
                          onClick={() => handleRequestSort(header.id)}
                        >
                          {header.label}
                        </TableSortLabel>
                      ) : (
                        header.label
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  Array.from({ length: page.page_count }).map((_, index) => (
                    <TableRow key={`skeleton-${index}`}>
                      {tableHeaders.map((header) => (
                        <TableCell
                          key={header.id}
                          align="center"
                          sx={{ py: 2 }}
                        >
                          <Box
                            sx={{
                              height: "20px",
                              bgcolor: "#f5f5f5",
                              borderRadius: "4px",
                              animation: "pulse 1.5s ease-in-out infinite",
                            }}
                          />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : Array.isArray(filteredData) && filteredData.length > 0 ? (
                  filteredData.map((item, index) => {
                    const daysSinceLastSale = item?.last_sale_days || 0;
                    const rowColor = getBackgroundColor(daysSinceLastSale);

                    return (
                      <TableRow
                        key={index}
                        sx={{
                          backgroundColor:
                            item.actual_stock === 0 ? "#fdf2f2" : rowColor,
                          "&:hover": {
                            bgcolor: "#fef7e0",
                          },
                        }}
                      >
                        <TableCell align="center" sx={{ fontSize: "0.875rem" }}>
                          {item?.partnerId || "N/A"}
                        </TableCell>
                        <TableCell align="center" sx={{ fontSize: "0.875rem" }}>
                          {item?.local || "N/A"}
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{ fontSize: "0.875rem", maxWidth: 200 }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {item?.title || "N/A"}
                          </Typography>
                        </TableCell>
                        <TableCell align="center" sx={{ fontSize: "0.875rem" }}>
                          <Typography
                            variant="body2"
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {item?.supplier || "N/A"}
                          </Typography>
                        </TableCell>
                        <TableCell align="center" sx={{ fontSize: "0.875rem" }}>
                          <Tooltip
                            title={`Faturamento 90 dias: ${item?.fat_90_days || "0,00"}`}
                          >
                            <span>{item?.quantity_sold_90_days || 0}</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="center" sx={{ fontSize: "0.875rem" }}>
                          <Tooltip
                            title={`Faturamento 30 dias: ${item?.fat_30_days || "0,00"}`}
                          >
                            <span>{item?.quantity_sold_30_days || 0}</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{ fontSize: "0.875rem", fontWeight: 600 }}
                        >
                          {item?.actual_stock || 0}
                        </TableCell>
                        <TableCell align="center" sx={{ fontSize: "0.875rem" }}>
                          {item?.last_sale
                            ? moment(item.last_sale).format("DD/MM/YYYY")
                            : "N/A"}
                        </TableCell>
                        <TableCell align="center" sx={{ fontSize: "0.875rem" }}>
                          {item?.last_sale_days || 0}
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            fontSize: "0.875rem",
                            fontWeight: 600,
                            color: "#2e7d32",
                          }}
                        >
                          {item?.unit_cost || "0,00"}
                        </TableCell>
                        <TableCell align="center" sx={{ fontSize: "0.875rem" }}>
                          {item?.stock_days || 0}
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            fontSize: "0.875rem",
                            fontWeight: 600,
                            color: "#2e7d32",
                          }}
                        >
                          {item?.stock_value || "0,00"}
                        </TableCell>
                        <TableCell align="center" sx={{ fontSize: "0.875rem" }}>
                          {item?.unit_purchase_order || 0}
                        </TableCell>
                        <TableCell align="center" sx={{ fontSize: "0.875rem" }}>
                          {item?.stock_days_plus_purchase_order || 0}
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip
                            title={
                              item.actual_stock === 0
                                ? "Sem estoque"
                                : "Em estoque"
                            }
                          >
                            {item.actual_stock === 0 ? (
                              <CancelIcon
                                sx={{ color: "#e57373", fontSize: 20 }}
                              />
                            ) : (
                              <CheckCircleIcon
                                sx={{ color: "#66bb6a", fontSize: 20 }}
                              />
                            )}
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={15} align="center" sx={{ py: 6 }}>
                      <InventoryIcon
                        sx={{ fontSize: 48, color: "#bdbdbd", mb: 2 }}
                      />
                      <Typography
                        variant="h6"
                        color="text.secondary"
                        sx={{ mb: 1 }}
                      >
                        Nenhum item encontrado
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Tente ajustar os filtros de pesquisa
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              p: 2,
              bgcolor: "white",
            }}
          >
            <TablePagination
              rowsPerPageOptions={[20, 50, 100]}
              component="div"
              count={data?.totalItems || 0}
              rowsPerPage={page.page_count}
              page={page.page_index}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              ActionsComponent={TablePaginationActions}
              labelRowsPerPage="Itens por página"
            />
          </Box>
        </Paper>
      </Box>
    </AccessGuard>
  );
};

export default StorageDashboard;
